import { CSSProperties } from 'react';

export const enum BgColorTypes {
  SOLID = 'solid',
  LINEAR_GRADIENT = 'linear-gradient',
  RADIAL_GRADIENT = 'radial-gradient',
}

export const enum ListStyles {
  DOT = 'dot',
  SQUARE = 'square',
  NUMBERED = 'numbered',
  POINTER = 'pointer',
  LIGHTBULB = 'lightbulb',
  EMOJI = 'emoji',
  NONE = 'none',
}

export const enum CardItemTypes {
  STORY_TITLE = 'STORY_TITLE',
  STORY_DESCRIPTION = 'STORY_DESCRIPTION',
  TEXT = 'TEXT',
  AUDIO = 'AUDIO',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  LINK = 'LINK',
  POLL = 'POLL',
  MULTIPLE_CHOICE_QUESTION = 'MULTIPLE_CHOICE_QUESTION',
  CHECK_LIST = 'CHECK_LIST',
  OPEN_QUESTION = 'OPEN_QUESTION',
  GIF = 'GIF',
}

export interface ICard {
  id?: string;
  meta: {
    order: number;
    isHidden?: boolean;
  };
  background: {
    type: BgColorTypes;
    colors: string; // color array as string for gradients -> "#000000,#ffffff"
  };
  items: TCardItem[];
}

export type TCardItem =
  | ITextCardItem
  | IAudioCardItem
  | IImageCardItem
  | IVideoCardItem
  | ILinkCardItem
  | IOpenQuestionCardItem
  | IMultipleChoiceCardItem
  | ICheckListCardItem
  | IPollCardItem
  | IGifCardItem;

interface IBaseCardItem {
  id: string;
  dimensions: IItemCSSDimensions;
  position?: IItemPosition;
}

interface IBaseCardItemMeta {
  version: 1;
  order?: number;
}

interface IStorageItemBody {
  path: string;
  type: string;
  name: string;
  link: string;
}

export interface ITextCardItem extends IBaseCardItem {
  meta: {
    type:
      | CardItemTypes.TEXT
      | CardItemTypes.STORY_TITLE
      | CardItemTypes.STORY_DESCRIPTION;
  } & IBaseCardItemMeta;
  body: {
    type: 'headline' | 'paragraph';
    display: 'p';
    content: string;
    fontSize: number;
    listStyle?: ListStyles;
    styles: Omit<CSSProperties, 'height'>;
  };
}

export interface IAudioCardItem extends IBaseCardItem {
  meta: {
    type: CardItemTypes.AUDIO;
  } & IBaseCardItemMeta;
  body: {
    isAutoplay: boolean;
    isLoop: boolean;
    isAutoContinue: boolean;
    showUI: boolean;
    volume: number;
    color: string;
  } & IStorageItemBody;
}

export interface ImageCrop {
  width: string;
  height: string;
  left: string;
  top: string;
  x: number;
  y: number;
  internalWidth: number;
  internalHeight: number;
  aspectRatio: number;
}

export interface IImageCardItem extends IBaseCardItem {
  meta: {
    type: CardItemTypes.IMAGE;
  } & IBaseCardItemMeta;
  body: {
    originalWidth: number;
    originalHeight: number;
    linkResized?: string;
    pathResized?: string;
    isFullCard: boolean;
    crop?: ImageCrop;
  } & IStorageItemBody;
}

export interface IVideoCardItem extends IBaseCardItem {
  meta: {
    type: CardItemTypes.VIDEO;
  } & IBaseCardItemMeta;
  body: {
    fireStoreId: string;
    fileName: string;
    isFullCard: boolean;
    isAutoplay: boolean;
    isLoop: boolean;
    isAutoContinue: boolean;
    showUI: boolean;
    volume: number;
    variant: 'light' | 'dark';
  };
}

export interface ILinkCardItem extends IBaseCardItem {
  meta: {
    type: CardItemTypes.LINK;
  } & IBaseCardItemMeta;
  body: {
    title: string;
    link: string;
    variant: 'dark' | 'light';
  };
}

export interface IGifCardItem extends IBaseCardItem {
  meta: {
    type: CardItemTypes.GIF;
  } & IBaseCardItemMeta;
  body: {
    originalWidth: string;
    originalHeight: string;
    isFullCard: boolean;
    imageUrl: string;
  };
}

export interface IOpenQuestionCardItem extends IBaseCardItem {
  meta: {
    type: CardItemTypes.OPEN_QUESTION;
  } & IBaseCardItemMeta;
  body: {
    questionId: string;
  };
}

export interface IMultipleChoiceCardItem extends IBaseCardItem {
  meta: {
    type: CardItemTypes.MULTIPLE_CHOICE_QUESTION;
  } & IBaseCardItemMeta;
  body: {
    questionId: string;
  };
}

export interface ICheckListCardItem extends IBaseCardItem {
  meta: {
    type: CardItemTypes.CHECK_LIST;
  } & IBaseCardItemMeta;
  body: {
    checklistId: string;
  };
}

export interface IPollCardItem extends IBaseCardItem {
  meta: {
    type: CardItemTypes.POLL;
  } & IBaseCardItemMeta;
  body: {
    pollId: string;
  };
}

export interface IItemPosition {
  top: string;
  left: string;
}

export interface IItemCSSDimensions {
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  aspectRatio?: number;
}

export interface IItemDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}
